/* CAFFREY DrySpell page behaviour (Claude Code, launch builder, 2026-10-07).
   Proof stage: "Watch the dip test" (or a tap on the still) plays CAFFREY's film in the same 4:5 box. It stops at
   data-end (40.1 s, while the caption still reads "DrySpell works"), then the still returns with "Watch again".
   The stop is checked on every decoded frame where requestVideoFrameCallback exists, and on timeupdate everywhere;
   seeking past it is clamped. The film pauses when it leaves the screen or the tab is hidden. Idempotent; supports the
   theme editor's section events. The buy box and sticky bar are the shared kit (cf-kit.js).
   Build r2 (Claude Code, 2026-10-07 UTC): the film's poster is attached when the film opens (see start()); the bead
   illustration and the proof still wait in <noscript data-ds-defer> until the hero has loaded (see watchHeld/armGate).
   Fix 1 (Claude Code, launch fixer, 2026-10-07 UTC): when the film opens, the whole frame is brought into view (see
   reveal()); the play button sits under the frame, so a tap high on a phone screen used to start the film with its
   top cut off (independent check: frame top at -72 px on a 390 px phone). */
(() => {
  if (window.CaffreyDrySpellPage) return;
  const films = new Map();

  function create(root) {
    const section = root.closest('.cf-ds-proof') || root.parentElement;
    const still = root.querySelector('[data-ds-still]');
    const movie = root.querySelector('[data-ds-movie]');
    const video = movie ? movie.querySelector('video') : null;
    const play = section ? section.querySelector('[data-ds-play]') : null;
    const label = play ? play.querySelector('[data-ds-play-label]') : null;
    if (!still || !movie || !video || !play) return null;
    const end = parseFloat(root.dataset.end) || 0;
    const state = { on: false, vfc: 0 };
    const limit = () => (end > 0 && (!video.duration || end < video.duration) ? end : 0);

    function cancelFrames() {
      if (state.vfc && video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(state.vfc);
      state.vfc = 0;
    }
    function finish() {
      if (!state.on) return;
      state.on = false;
      cancelFrames();
      video.pause();
      movie.hidden = true;
      still.hidden = false;
      root.classList.remove('is-playing');
      play.classList.remove('is-quiet');
      if (label) label.textContent = root.dataset.again || root.dataset.watch || label.textContent;
      try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
    }
    // Fix 1: scroll only as far as needed to show the whole frame. scroll-margin (cf-ds-page.css) keeps it clear of the
    // store header, which slides back in on an upward scroll, and of the kit sticky bar; instant under reduced motion.
    function reveal() {
      if (!root.getBoundingClientRect || !root.scrollIntoView) return;
      const r = root.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const cs = window.getComputedStyle(root);
      const mt = parseFloat(cs.scrollMarginTop) || 0;
      const mb = parseFloat(cs.scrollMarginBottom) || 0;
      if (r.top >= mt && r.bottom <= vh - mb) return;
      const calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try { root.scrollIntoView({ block: 'nearest', behavior: calm ? 'auto' : 'smooth' }); } catch (e) { root.scrollIntoView(false); }
    }
    function watchFrames() {
      if (!video.requestVideoFrameCallback) return;
      const tick = (now, meta) => {
        if (!state.on) return;
        const l = limit();
        if (l && meta && meta.mediaTime >= l) { finish(); return; }
        state.vfc = video.requestVideoFrameCallback(tick);
      };
      cancelFrames();
      state.vfc = video.requestVideoFrameCallback(tick);
    }
    function start() {
      // the poster is attached now, not in the HTML (a poster attribute is fetched at parse time and slowed the hero):
      // prefer the still already on screen (same 4:5 frame, already decoded), else the data-poster file
      if (!video.getAttribute('poster')) {
        const img = still.querySelector('img');
        const src = (img && img.currentSrc) || video.dataset.poster;
        if (src) video.setAttribute('poster', src);
      }
      state.on = true;
      still.hidden = true;
      movie.hidden = false;
      root.classList.add('is-playing');
      play.classList.add('is-quiet');
      reveal();
      const l = limit();
      try { if (l && video.currentTime >= l - 0.05) video.currentTime = 0; } catch (e) { /* ignore */ }
      const p = video.play();
      if (p && p.catch) p.catch(() => { /* blocked or interrupted: native controls stay available */ });
      watchFrames();
    }
    const onPlayClick = () => { if (!state.on) start(); };
    const onStillClick = (e) => { if (!state.on && !e.defaultPrevented) start(); };
    const onTime = () => { const l = limit(); if (state.on && l && video.currentTime >= l) finish(); };
    const onSeeking = () => { const l = limit(); if (l && video.currentTime > l) { try { video.currentTime = Math.max(0, l - 0.1); } catch (e) { /* ignore */ } } };
    const onPlaying = () => { if (state.on) watchFrames(); };
    const onEnded = () => finish();
    play.addEventListener('click', onPlayClick);
    still.addEventListener('click', onStillClick);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('ended', onEnded);
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (!entry.isIntersecting && state.on) video.pause(); });
    }) : null;
    if (io) io.observe(root);
    return {
      pause() { if (state.on) video.pause(); },
      destroy() {
        cancelFrames();
        if (io) io.disconnect();
        play.removeEventListener('click', onPlayClick);
        still.removeEventListener('click', onStillClick);
        video.removeEventListener('timeupdate', onTime);
        video.removeEventListener('seeking', onSeeking);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('ended', onEnded);
        video.pause();
      }
    };
  }

  // Below-the-fold images held in <noscript data-ds-defer> (build r2). They are inserted once the first-screen photo
  // (the LCP image) has loaded, or after 4 s at the latest, and only when they are within reach of the screen, so on slow
  // phones they no longer share bandwidth with the hero. Without JavaScript the browser renders the noscript images.
  const held = new Set();
  let gateOpen = false;
  let io = null;
  function inject(box) {
    box.querySelectorAll(':scope > noscript[data-ds-defer]').forEach((ns) => {
      const t = document.createElement('template');
      t.innerHTML = ns.textContent;
      ns.replaceWith(t.content);
    });
  }
  function observe(box) {
    if (!('IntersectionObserver' in window)) { inject(box); return; }
    if (!io) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) { io.unobserve(entry.target); inject(entry.target); } });
      }, { rootMargin: '600px 0px 1200px 0px' });
    }
    io.observe(box);
  }
  function openGate() {
    if (gateOpen) return;
    gateOpen = true;
    held.forEach((box) => { if (box.isConnected) observe(box); });
  }
  function watchHeld() {
    document.querySelectorAll('noscript[data-ds-defer]').forEach((ns) => {
      const box = ns.parentElement;
      if (!box || held.has(box)) return;
      held.add(box);
      if (gateOpen) observe(box);
    });
  }
  function armGate() {
    const hero = document.querySelector('.cf-ds-hero__img');
    if (!hero || hero.complete) { openGate(); return; }
    hero.addEventListener('load', openGate, { once: true });
    hero.addEventListener('error', openGate, { once: true });
    setTimeout(openGate, 4000);
  }

  function initialise() {
    for (const [root, film] of films) if (!root.isConnected) { film.destroy(); films.delete(root); }
    watchHeld();
    document.querySelectorAll('[data-ds-film]').forEach((root) => {
      if (films.has(root)) return;
      const film = create(root);
      if (film) films.set(root, film);
    });
  }

  document.addEventListener('visibilitychange', () => { if (document.hidden) films.forEach((film) => film.pause()); });
  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, film] of films) if (event.target.contains(root)) { film.destroy(); films.delete(root); }
  });
  document.addEventListener('shopify:section:reorder', initialise);
  window.CaffreyDrySpellPage = { initialise };
  const boot = () => { initialise(); armGate(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
